import React, { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { formatDateTime, formatNumberWithCommas, isPermission } from "../../common/utility"
import {
  Button,
  Card,
  CardBody,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from "reactstrap"
import TableContainerV1 from "components/Common/TableContainerV1"
import ToastDialog from "components/Common/ToastDialog"
import { getProductsList } from "http/products/products"
import { getGeneralMaster } from "http/master/master"
import { AiOutlineSync } from "react-icons/ai"
import { Tooltip } from 'react-tooltip';
import { getApiLogs } from "http/apiLogs/apiLogs"
import FullPageLoader from "components/Common/FullPageLoader"


const ProductTable = () => {
  document.title = "Products List | DMS"

  const navigate = useNavigate()
  const [toastInfo, setToastInfo] = useState({ showToast: false })
  const [productList, setProductList] = useState([])
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageCount: 0,
    totalRecords: 0,
  })
  const [pageSize, setPageSize] = useState(10)
  const [searchValue, setSearchValue] = useState("")
  const [columnId, setColumnId] = useState("id")
  const [sortingOrderStatus, setSortingOrderStatus] = useState("desc")
  const [sortingOptions, setSortingOptions] = useState({
    column: "id",
    isAsc: false,
    optionalColumn: ["Action", "productImages", "subCategory"],
  })
  const [isSyncing, setIsSyncing] = useState(false)
  const [lastSynced, setLastSynced] = useState(null);
  const {roleName}= JSON.parse(localStorage.getItem("user"))
  const [syncPermission, setSyncPermission] = useState(false);

  const TableActionsComponent = (props, entityType) => {
    const navigate = useNavigate()
    return (
      <UncontrolledDropdown>
        <DropdownToggle tag="a" className="card-drop">
          <i className="mdi mdi-dots-horizontal font-size-18"></i>
        </DropdownToggle>

        <DropdownMenu className="dropdown-menu-end">
          <DropdownItem
            onClick={() => {
              navigate(`/${roleName}/product-detail/${props.id}`)
            }}
          >
            <i
              className="mdi mdi-eye font-size-16 text-success me-1"
              id="viewtooltip"
            ></i>
            View
            <UncontrolledTooltip placement="top" target="viewtooltip">
              View
            </UncontrolledTooltip>
          </DropdownItem>
          <DropdownItem
            onClick={() => {
              navigate(`/${roleName}/addProduct?id=${props.id}`)
            }}
          >
            <i
              className="mdi mdi-pencil font-size-16 text-success me-1"
              id="edittooltip"
            ></i>
            Edit
            <UncontrolledTooltip placement="top" target="edittooltip">
              Edit
            </UncontrolledTooltip>
          </DropdownItem>
        </DropdownMenu>
      </UncontrolledDropdown>
    )
  }

  const columns = useMemo(() => [
    {
      Header: "Id",
      accessor: "id",
      filterable: true,
      Cell: cellProps => {
        return cellProps.value || "-"
      },
    },
    {
      Header: "Product Name",
      accessor: "productName",
      filterable: true,
      Cell: cellProps => {
        return cellProps.value || "-"
      },
    },
    {
      Header: "Product Description",
      accessor: "productDescription",
      filterable: true,
      width: "300px",
      Cell: cellProps => {
        const maxLength = 50
        const text = cellProps.value || "-"

        const truncatedText = text.length > maxLength
          ? `${text.substring(0, maxLength)}...`
          : text

        return (
          <div>
            <span data-tooltip-id={`my-tooltip-${cellProps?.row.original?.id}`}>{truncatedText}</span>
            <Tooltip
              style={{ maxWidth: '500px', marginLeft: '1rem' }}
              id={`my-tooltip-${cellProps?.row.original?.id}`}
              place="top"
              content={text}
            />
          </div>
        );
      },
    },
    {
      Header: "Product Code",
      accessor: "sku",
      filterable: true,
      Cell: cellProps => {
        return cellProps.value || "-"
      },
    },
    {
      Header: "Image",
      accessor: "productImages",
      filterable: true,
      Cell: cellProps => {
        const rowData = cellProps.row.original
        if (rowData?.image) {
          return (
            <img
              style={{ width: "50px" }}
              src={
                process.env.REACT_APP_IMAGE_URL + rowData.image?.paths?.original
              }
            />
          )
        } else {
          return (<img
            style={{ width: "50px" }}
            alt={rowData.productName}
            src={
              '#'
            }
          />)
        }
      },
    },
    {
      Header: "Category",
      accessor: "category",
      filterable: true,
      Cell: cellProps => {
        return cellProps?.row?.original?.categoryName || "-"
      },
    },
    {
      Header: "Sub Category",
      accessor: "subCategory",
      filterable: true,
      Cell: cellProps => {
        return (
          cellProps?.row?.original?.subCategoryName ||
          "-"
        )
      },
    },
    {
      Header: "Selling Price",
      accessor: "sellingPrice",
      filterable: true,
      Cell: (cellProps) => {
        const value = parseFloat(cellProps.value);
        return (
            <div style={{ textAlign: "right" }}>{value ? formatNumberWithCommas(value) : "-"}</div>
        )
        
      },
    },
    {
      Header: "Action",
      Cell: cellProps => {
        const rowData = cellProps.row.original
        return TableActionsComponent(rowData)
      },
    },
  ])

  const fetchProductsList = async () => {
    try {
      const { data: responseData } = await getProductsList({ limit: 10})

      const {
        currentPage,
        totalPages,
        totalItems,
        data: productList,
      } = responseData
      setPagination({
        pageIndex: currentPage,
        pageCount: totalPages,
        totalRecords: totalItems,
      })
      setProductList(productList)
    } catch (error) {
      console.error("Error fetching product list:", error.message)
    }
  }
  const handlePageChange = async (
    page,
    pageSize,
    searchKey = searchValue,
    columnName = columnId,
    sortingOrder = sortingOrderStatus
  ) => {
    setPageSize(pageSize)

    const {
      data: responseData,
      status,
    } = await getProductsList({
      page,
      limit: pageSize,
      searchKey,
      columnName,
      sortingOrder,
    })
    if (status) {
      const {
        currentPage,
        totalPages,
        totalItems,
        data: productList,
      } = responseData
      if (productList.length > 0) {
        setPagination({
          pageIndex: currentPage,
          pageCount: totalPages,
          totalRecords: totalItems,
        })
        setProductList(productList)
      } else {
        setProductList([])
        setPagination({
          pageIndex: 0,
          pageCount: 0,
          totalRecords: 0,
        })
      }
    } else {
      setProductList([])
      setPagination({
        pageIndex: 0,
        pageCount: 0,
        totalRecords: 0,
      })
    }
  }

  const handleSortColumn = option => {
    setSortingOptions(option)
    const columnName = option.column
    setColumnId(columnName)
    const sortingOrder = option.isAsc ? "asc" : "desc"
    setSortingOrderStatus(sortingOrder)
    handlePageChange(1, pageSize, searchValue, columnName, sortingOrder)
  }

  const handleSearchFilter = (value, pageSize) => {
    const newValue = value !== undefined ? value : "";
    setSearchValue(newValue);
    handlePageChange(1, pageSize, newValue)
  }
  const handleClearSearch = () => {
    setSearchValue("")
    handlePageChange(1, pageSize, "")
  }

  useEffect(() => {
    fetchProductsList()
    fetchLastSynced()
    checkPermission()
  }, [])

  const checkPermission = async () => {
    let isSyncPermission = await isPermission('sync', 'create_permission')
    setSyncPermission(isSyncPermission)
  }

  const handleAddProduct = () => {
    navigate("/admin/addProduct")
  }

  const handleSync = async () => {
    setIsSyncing(true)
    try {
      let res = await getGeneralMaster({ masterName: "product" })
      if (res.status) {
        setToastInfo({
          message: res.message,
          showToast: true,
          role: "success",
        })
        fetchProductsList()
        setIsSyncing(false)
        fetchLastSynced()
      } else {
        setToastInfo({
          message: "Something went wrong",
          showToast: true,
          role: "error",
        })
        setIsSyncing(false);
      }
    } catch (err) {
      setToastInfo({
        message: "Something went wrong",
        showToast: true,
        role: "error",
      })
      setIsSyncing(false)
    }
  }

  const fetchLastSynced = async () => {
    try {
      let res = await getApiLogs({ masterName: 'product_master' })
      if (res.status) {
        setLastSynced(formatDateTime(res.data.initialDateTime))
      }
    } catch (err) {
      console.error(err)
    }
  }

  const buttonSection = (
    <>
      {syncPermission && (<div>
        <div className="d-flex justify-content-end mb-1">
          <button
            className="btn btn-success"
            type="button"
            onClick={handleSync}
          >
            <AiOutlineSync size={15} className={isSyncing ? 'rotating' : ''} /> Sync Data
          </button>
        </div>
        {lastSynced && <p className="d-flex justify-content-end ">Last Synced: {lastSynced}</p>}
      </div>)}
    </>
  )

  return (
    <>
      {isSyncing && <FullPageLoader />}
      <Card>
        <CardBody>
          <TableContainerV1
            columns={columns}
            data={productList}
            pagination={pagination}
            handlePageChange={handlePageChange}
            searchHelperText="Search by Product Name, Category, Sub Category, Product Code"
            handleSearchFilter={handleSearchFilter}
            sortOptions={sortingOptions}
            customSection={buttonSection}
            handleSortColumn={handleSortColumn}
            handleClearSearch={handleClearSearch}
            className="custom-header-css"
          />
        </CardBody>
      </Card>

      <ToastDialog
        message={toastInfo.message}
        showToast={toastInfo.showToast}
        role={toastInfo.role}
        onCloseFn={() => setToastInfo({ showToast: false })}
      />
    </>
  )
}

export default ProductTable
